import React, { useState, useEffect } from 'react';
import {
  Button, TextField, Box, Typography, IconButton, Switch, FormControlLabel,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Paper
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';

const OAUTH_LOGIN_BASE = 'https://trade.shoonya.com/OAuthlogin/authorize/oauth';

const emptyAccount = {
  account_name: '', user: '', pwd: '', client_id: '', secret_key: '',
  multiplier: '', active: false, access_token: null, token_expiry: null, proxy_url: '',
};

const ShoonyaPlaceAccount = () => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountNames, setAccountNames] = useState([]);
  const [accountDetails, setAccountDetails] = useState(emptyAccount);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [tokenMessage, setTokenMessage] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);

  useEffect(() => {
    const fetchNames = async () => {
      const names = [];
      for (let i = 1; i <= 10; i++) {
        const account = `Shoonya${i}`;
        try {
          const r = await axios.get(`/api/get_order_account/${account}`);
          names.push(r.data?.account_name || account);
        } catch { names.push(account); }
      }
      setAccountNames(names);
    };
    fetchNames();
  }, []);

  useEffect(() => {
    if (selectedAccount) fetchAccountDetails(selectedAccount);
  }, [selectedAccount]);

  const fetchAccountDetails = async (account) => {
    try {
      const r = await axios.get(`/api/get_order_account/${account}`);
      setAccountDetails(Object.keys(r.data || {}).length > 0 ? { ...emptyAccount, ...r.data } : emptyAccount);
    } catch { setAccountDetails(emptyAccount); }
  };

  const handleSave = async () => {
    const { account_name, user, pwd, client_id, secret_key, multiplier } = accountDetails;
    if (!account_name || !user || !pwd || !client_id || !secret_key || !multiplier) {
      setMessage('All fields are required'); return;
    }
    try {
      await axios.post(`/api/save_order_account/${selectedAccount}`, accountDetails);
      setMessage('Account saved successfully!');
      const updated = [...accountNames];
      updated[parseInt(selectedAccount.replace('Shoonya', '')) - 1] = accountDetails.account_name;
      setAccountNames(updated);
    } catch { setMessage('Error saving account.'); }
  };

  const handleInput = (field, value) => setAccountDetails(prev => ({ ...prev, [field]: value }));

  const openTokenDialog = () => {
    if (!accountDetails.client_id) { setMessage('Save client_id first.'); return; }
    setRedirectUrl(''); setTokenMessage(''); setTokenDialogOpen(true);
    window.open(`${OAUTH_LOGIN_BASE}?client_id=${accountDetails.client_id}`, '_blank');
  };

  const handleGenerateToken = async () => {
    let code = redirectUrl.trim();
    if (code.includes('code=')) code = code.split('code=')[1].split('&')[0].trim();
    if (!code) { setTokenMessage('Paste the redirect URL or code.'); return; }
    setTokenLoading(true);
    try {
      const r = await axios.post(`/api/shoonya_generate_token/${selectedAccount}`, { code });
      setTokenMessage(r.data.message);
      await fetchAccountDetails(selectedAccount);
    } catch (err) {
      setTokenMessage(err.response?.data?.error || 'Token generation failed.');
    } finally { setTokenLoading(false); }
  };

  const accountIdx = selectedAccount ? parseInt(selectedAccount.replace('Shoonya', '')) - 1 : -1;

  return (
    <Box>
      {/* Account Selector */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Select Account</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Array.from({ length: 10 }, (_, i) => {
            const acct = `Shoonya${i + 1}`;
            const isSelected = selectedAccount === acct;
            return (
              <Chip
                key={i}
                label={accountNames[i] || `Shoonya ${i + 1}`}
                onClick={() => setSelectedAccount(acct)}
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: isSelected ? 'primary.main' : 'transparent',
                  color: isSelected ? '#fff' : 'text.secondary',
                  borderColor: isSelected ? 'primary.main' : '#e2e8f0',
                  fontWeight: isSelected ? 600 : 400,
                  '&:hover': { bgcolor: isSelected ? 'primary.dark' : '#f8fafc' },
                  cursor: 'pointer',
                }}
              />
            );
          })}
        </Box>
      </Box>

      {selectedAccount && (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Form */}
          <Box sx={{ flex: 1, maxWidth: 520 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {accountNames[accountIdx] || selectedAccount}
            </Typography>
            <Stack spacing={1.5}>
              <TextField label="Account Name" value={accountDetails.account_name}
                onChange={e => handleInput('account_name', e.target.value)} fullWidth />
              <TextField label="User ID" value={accountDetails.user}
                onChange={e => handleInput('user', e.target.value)} fullWidth />
              <TextField label="Password" value={accountDetails.pwd}
                onChange={e => handleInput('pwd', e.target.value)} fullWidth
                type={showPassword ? 'text' : 'password'}
                InputProps={{ endAdornment: (
                  <IconButton size="small" onClick={() => setShowPassword(p => !p)} edge="end">
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                )}} />
              <TextField label="Client ID (e.g. FA64850_U)" value={accountDetails.client_id}
                onChange={e => handleInput('client_id', e.target.value)} fullWidth
                helperText="From trade.shoonya.com API portal — Client Id field" />
              <TextField label="Secret Key" value={accountDetails.secret_key}
                onChange={e => handleInput('secret_key', e.target.value)} fullWidth
                type={showSecretKey ? 'text' : 'password'}
                helperText="From trade.shoonya.com API portal — Secret Code field"
                InputProps={{ endAdornment: (
                  <IconButton size="small" onClick={() => setShowSecretKey(p => !p)} edge="end">
                    {showSecretKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                )}} />
              <TextField label="Multiplier" value={accountDetails.multiplier}
                onChange={e => handleInput('multiplier', e.target.value)} fullWidth type="number" />
              <TextField label="Proxy URL (Webshare)" value={accountDetails.proxy_url || ''}
                onChange={e => handleInput('proxy_url', e.target.value)} fullWidth
                placeholder="http://user:pass@host:port"
                helperText="Webshare dedicated proxy for this account's REST calls" />
              <FormControlLabel
                control={<Switch checked={!!accountDetails.active} onChange={() => handleInput('active', !accountDetails.active)} />}
                label={<Typography variant="body2">{accountDetails.active ? 'Active' : 'Inactive'}</Typography>}
              />
            </Stack>

            <Button variant="contained" onClick={handleSave} sx={{ mt: 3, px: 4 }}>
              Save Account
            </Button>
            {message && (
              <Typography variant="body2" sx={{ mt: 1.5, color: message.includes('Error') ? 'error.main' : 'success.main' }}>
                {message}
              </Typography>
            )}
          </Box>

          {/* Token Panel */}
          <Paper sx={{ width: 260, p: 2.5, borderRadius: 2, flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>OAuth Token</Typography>
            {accountDetails.access_token ? (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#059669' }} />
                  <Typography variant="body2" sx={{ color: '#059669', fontWeight: 600 }}>Token active</Typography>
                </Box>
                {accountDetails.token_expiry && (
                  <Typography variant="caption" color="text.secondary">
                    Expires: {accountDetails.token_expiry}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {accountDetails.access_token.slice(0, 24)}…
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No token stored.</Typography>
            )}
            <Button variant="outlined" color="primary" onClick={openTokenDialog} fullWidth size="small">
              {accountDetails.access_token ? 'Regenerate Token' : 'Generate Token'}
            </Button>
          </Paper>
        </Box>
      )}

      {/* Token Dialog */}
      <Dialog open={tokenDialogOpen} onClose={() => setTokenDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Generate OAuth Token — {selectedAccount}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            A Shoonya login page opened in a new tab. Log in, then copy the full redirect URL
            (starts with <strong>https://www.google.com/?code=</strong>) and paste below.
          </Typography>
          <TextField
            label="Redirect URL or Code"
            value={redirectUrl}
            onChange={e => setRedirectUrl(e.target.value)}
            fullWidth
            placeholder="https://www.google.com/?code=xxxxxxxx-xxxx-xxxx-xxxx"
            multiline rows={2}
          />
          {tokenMessage && (
            <Typography variant="body2"
              sx={{ mt: 1.5, color: tokenMessage.toLowerCase().includes('success') ? 'success.main' : 'error.main' }}>
              {tokenMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTokenDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleGenerateToken} disabled={tokenLoading}>
            {tokenLoading ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShoonyaPlaceAccount;
